import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, X, Download } from 'lucide-react';
import { Button, Dialog, Input, Select, Textarea } from '@/design-system';
import {
  managerConnectorService,
  type GatewayLogRequest,
  type LogRequestStatus,
} from './api/connector-gateways.service';

interface Props {
  gatewayId: string;
  canManage: boolean;
}

const STATUS_COLORS: Record<LogRequestStatus, string> = {
  requested: 'bg-blue-100 text-blue-700',
  active: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-gray-100 text-gray-700',
  uploaded: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_LABEL: Record<LogRequestStatus, string> = {
  requested: 'aguardando sidecar',
  active: 'ativo',
  expired: 'expirado',
  uploaded: 'pronto',
  cancelled: 'cancelado',
};

/**
 * P3.4 (issue #62): UI staff pra ativar verbose log mode num gateway
 * e baixar bundle de logs uploaded.
 */
export function VerboseLogControl({ gatewayId, canManage }: Props) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [level, setLevel] = useState<'trace' | 'debug' | 'info'>('trace');
  const [durationMin, setDurationMin] = useState(15);
  const [reason, setReason] = useState('');

  const listQuery = useQuery({
    queryKey: ['gateway-log-requests', gatewayId],
    queryFn: () => managerConnectorService.listLogRequests(gatewayId),
    enabled: !!gatewayId,
    refetchInterval: 15_000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      level: 'trace' | 'debug' | 'info';
      durationMin: number;
      reason?: string;
    }) => managerConnectorService.createLogRequest(gatewayId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateway-log-requests', gatewayId] });
      setDialogOpen(false);
      setReason('');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (requestId: string) =>
      managerConnectorService.cancelLogRequest(gatewayId, requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['gateway-log-requests', gatewayId] });
    },
  });

  const requests = listQuery.data ?? [];

  if (!canManage && requests.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-violet-600" aria-hidden="true" />
          <h4 className="text-sm font-semibold text-gray-900">Verbose log (DLQ debug)</h4>
        </div>
        {canManage && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogOpen(true)}
            disabled={createMutation.isPending}
          >
            Ativar…
          </Button>
        )}
      </div>

      {listQuery.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-500">Nenhum pedido recente.</p>
      ) : (
        <ul className="space-y-1.5">
          {requests.map((r) => (
            <LogRequestRow
              key={r.id}
              request={r}
              canManage={canManage}
              onCancel={() => cancelMutation.mutate(r.id)}
              cancelling={cancelMutation.isPending}
            />
          ))}
        </ul>
      )}

      <Dialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Ativar verbose log mode"
        footer={(
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)} disabled={createMutation.isPending}>Cancelar</Button>
            <Button size="sm" variant="primary" disabled={createMutation.isPending} onClick={() => createMutation.mutate({ level, durationMin, reason: reason.trim() || undefined })}>
              {createMutation.isPending ? 'Criando…' : 'Ativar'}
            </Button>
          </div>
        )}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Sidecar vai aplicar o filter de log indicado por N minutos.
            Bundle de logs é uploaded automaticamente ao expirar.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Nível
              </label>
              <Select
                value={level}
                onChange={(e) => setLevel(e.target.value as typeof level)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="trace">trace</option>
                <option value="debug">debug</option>
                <option value="info">info</option>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Duração (min)
              </label>
              <Input
                type="number"
                min={5}
                max={240}
                value={durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value))}
                className="block"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700">
              Motivo (opcional)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex.: investigar timeout em handler X"
              rows={2}
              maxLength={280}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
}

function LogRequestRow({
  request,
  canManage,
  onCancel,
  cancelling,
}: {
  request: GatewayLogRequest;
  canManage: boolean;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const created = new Date(request.createdAt).toLocaleString('pt-BR');
  const isPendingOrActive =
    request.status === 'requested' || request.status === 'active';
  const downloadable = request.status === 'uploaded' && !!request.bundleUrl;

  return (
    <li className="flex items-start justify-between gap-3 rounded-lg bg-gray-50/60 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[request.status]}`}
          >
            {STATUS_LABEL[request.status]}
          </span>
          <span className="font-mono text-xs text-gray-700">
            {request.level} · {request.durationMin}min
          </span>
        </div>
        {request.reason && (
          <p className="mt-0.5 text-xs text-gray-600">{request.reason}</p>
        )}
        <p className="mt-0.5 text-xs text-gray-400">{created}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {downloadable && (
          <a
            href={request.bundleUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-violet-300 px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50"
          >
            <Download size={12} aria-hidden="true" />
            Baixar
          </a>
        )}
        {canManage && isPendingOrActive && (
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            disabled={cancelling}
            className="border-gray-300 text-gray-600 hover:bg-gray-100"
            aria-label="Cancelar pedido"
          >
            <X size={12} aria-hidden="true" />
          </Button>
        )}
      </div>
    </li>
  );
}
